// --- START OF FILE SendMessageModal.js ---

import React, { useState, useEffect, useRef } from 'react';
import { getApiUrl } from '../config';
import './SendMessageModal.css';

/**
 * Модальное окно для отправки сообщения другому пользователю
 * @param {string} senderId - ID текущего пользователя (отправителя)
 * @param {object} recipient - Объект с данными получателя
 * @param {string} defaultMessage - Текст сообщения по умолчанию (опционально)
 * @param {function} onClose - Функция для закрытия модального окна
 */
const SendMessageModal = ({ senderId, recipient, defaultMessage = '', onClose }) => {
  const [messageText, setMessageText] = useState(defaultMessage);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const MAX_LENGTH = 300; // Максимальная длина сообщения
  
  const textareaRef = useRef(null);

  useEffect(() => {
    if (defaultMessage) {
      setMessageText(defaultMessage);
    }
  }, [defaultMessage]);
  
  useEffect(() => {
    if (textareaRef.current && !success) { // Фокусируемся, только если не показано сообщение об успехе
      textareaRef.current.focus();
      const length = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, [success]); // Перезапускаем эффект, если изменилось состояние success

  const handleSendMessage = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const trimmedMessage = messageText.trim();
      if (trimmedMessage.length === 0) {
        throw new Error('Сообщение не может быть пустым.');
      }
      if (trimmedMessage.length > MAX_LENGTH) {
        throw new Error(`Сообщение слишком длинное (макс. ${MAX_LENGTH} символов)`);
      }

      const formData = new FormData();
      formData.append('sender_telegram_id', senderId);
      formData.append('receiver_telegram_id', recipient.telegram_id);
      formData.append('message_text', trimmedMessage);

      const response = await fetch(getApiUrl('/api/save_notification_message'), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Не удалось отправить сообщение' }));
        throw new Error(errorData.error || 'Не удалось отправить сообщение');
      }

      setSuccess(true);
      setMessageText(''); 
      setTimeout(onClose, 1500);

    } catch (err) {
      console.error('Ошибка отправки сообщения:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Кнопка "Отправить" теперь здесь, если не сообщение об успехе */}
        {!success && (
          <button
            className="modal-send-button-top-right" // Новый класс для стилизации
            onClick={handleSendMessage}
            disabled={loading || messageText.trim().length === 0 || messageText.length > MAX_LENGTH}
          >
            {loading ? 'Отправка...' : 'Отправить'}
          </button>
        )}

        {!success ? (
          <>
            {/* Кнопка "X" для закрытия УДАЛЕНА */}
            <h3>{defaultMessage ? 'Отклик на активность' : 'Написать'} {recipient.name || recipient.username}</h3>
            <textarea
              ref={textareaRef}
              className="message-textarea"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Введите ваше сообщение и адрес в Telegram, если хотите пообщаться в Telegram"
              maxLength={MAX_LENGTH + 10} // Позволяем ввести чуть больше, чтобы показать ошибку, валидация при отправке
              rows="4"
            />
            <div style={{textAlign: 'right', fontSize: '12px', color: messageText.length > MAX_LENGTH ? 'red' : '#888', marginBottom:'10px'}}>
              {messageText.length} / {MAX_LENGTH}
            </div>
            {error && <p className="modal-error">{error}</p>}
            {/* Старая кнопка "Отправить" (снизу) УДАЛЕНА */}
          </>
        ) : (
          <div className="modal-success">
            <p>✅ Сообщение отправлено!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendMessageModal;